/**
 * Group Company Default Preferences card (CR-6256).
 *
 * The primary reference point for Admin Setup > Regional Settings: it states
 * the group-wide currency, timezone and date format that every site inherits
 * unless it holds its own value — and lets an admin change them.
 *
 * Each field can also be cleared back to "use platform default", which is what
 * the nullable columns on the group mean. Without an editor here a value set on
 * the group was visible, outranked the platform default, and could not be
 * changed from anywhere in the app.
 */
import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Switch } from '../../ui/switch';
import { Building2, Loader2, Coins, Clock, CalendarDays, AlertCircle, Save } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { GroupRegionalDefaults, GroupRegionalDefaultsUpdate, PreferencesChoices } from './types';

interface GroupDefaultsCardProps {
  groupDefaults: GroupRegionalDefaults | null;
  choices: PreferencesChoices | null;
  loading: boolean;
  saving: boolean;
  /** Set when the fetch failed, so the card doesn't read as "nothing configured". */
  error?: string | null;
  onSave: (values: GroupRegionalDefaultsUpdate) => void;
}

type FieldKey = 'currency' | 'timezone' | 'date_format';

/** The three defaults the ticket calls out, in the order it lists them. */
const DEFAULT_FIELDS: {
  key: FieldKey;
  label: string;
  icon: LucideIcon;
  payloadKey: 'default_currency' | 'default_timezone' | 'default_date_format';
}[] = [
  { key: 'currency', label: 'Currency', icon: Coins, payloadKey: 'default_currency' },
  { key: 'timezone', label: 'Timezone', icon: Clock, payloadKey: 'default_timezone' },
  { key: 'date_format', label: 'Date Format', icon: CalendarDays, payloadKey: 'default_date_format' },
];

export function GroupDefaultsCard({
  groupDefaults,
  choices,
  loading,
  saving,
  error,
  onSave,
}: GroupDefaultsCardProps) {
  // Pending edits only; '' means "inherit from above".
  const [edits, setEdits] = useState<Partial<Record<FieldKey, string>>>({});
  // Pending edit to the group judging clock (W2-T1); undefined = untouched.
  const [clockEdit, setClockEdit] = useState<boolean | undefined>(undefined);
  const [targetGroupId, setTargetGroupId] = useState<number | null>(null);

  const groupName = groupDefaults?.group_name;
  const editable = !!groupDefaults?.editable;
  const levels = groupDefaults?.available_groups ?? [];
  // Defaults to the admin's own group (the first entry).
  const target = levels.find((g) => g.id === targetGroupId) ?? levels[0] ?? null;
  const isTopLevel = !!target && target.id === levels[levels.length - 1]?.id;

  const optionsFor = (key: FieldKey) => {
    if (key === 'currency') return choices?.currencies ?? [];
    if (key === 'timezone') return choices?.timezones ?? [];
    return choices?.date_formats ?? [];
  };

  const valueFor = (key: FieldKey): string =>
    edits[key] ?? target?.values?.[key] ?? '';

  const groupClock = clockEdit ?? target?.values?.judge_on_group_clock ?? false;
  const groupTimezoneDisplay = groupDefaults?.settings?.timezone?.display || 'group timezone';
  const dirty = Object.keys(edits).length > 0 || clockEdit !== undefined;

  const handleSave = () => {
    const payload: GroupRegionalDefaultsUpdate = {};
    if (target) payload.group_id = target.id;
    DEFAULT_FIELDS.forEach(({ key, payloadKey }) => {
      if (edits[key] === undefined) return;
      // Empty selection clears this level's value so the field inherits again.
      payload[payloadKey] = edits[key] === '' ? null : edits[key];
    });
    if (clockEdit !== undefined && target?.id === levels[0]?.id) payload.judge_on_group_clock = clockEdit;
    onSave(payload);
    setEdits({});
    setClockEdit(undefined);
  };

  const handleTargetChange = (id: number) => {
    setTargetGroupId(id);
    setEdits({}); // pending edits belonged to the previous level
    setClockEdit(undefined);
  };

  return (
    <Card
      data-testid="group-defaults-card"
      className="border-blue-200 bg-blue-50/50 dark:border-blue-900 dark:bg-blue-950/20"
    >
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="h-5 w-5" />
          Group Company Default Preferences
          <Badge variant="outline" className="ml-auto">Applies to all sites</Badge>
        </CardTitle>
        <CardDescription>
          {groupName
            ? `The defaults for ${groupName}. Every site below uses them unless it has its own value.`
            : 'The defaults every site below uses unless it has its own value.'}
          {' '}Clear a field to inherit it from the parent group, or from Platform Admin at the top level.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="flex items-start gap-2 py-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <p>{error}</p>
          </div>
        ) : !groupDefaults || Object.keys(groupDefaults.settings).length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">
            No group defaults available. Group defaults appear once a company is in scope.
          </p>
        ) : (
          <div className="space-y-4">
            {/* Only worth showing when there is more than one level to choose. */}
            {editable && levels.length > 1 && (
              <div className="space-y-2 max-w-sm">
                <Label htmlFor="group-defaults-level">Set defaults for</Label>
                <select
                  id="group-defaults-level"
                  value={target?.id ?? ''}
                  disabled={saving}
                  onChange={(e) => handleTargetChange(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background"
                >
                  {levels.map((g, i) => (
                    <option key={g.id} value={g.id}>
                      {g.name}{i === 0 ? ' (this company’s group)' : ''}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground">
                  A value set on a parent group applies to every group beneath it.
                </p>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {DEFAULT_FIELDS.map(({ key, label, icon: Icon }) => {
                const entry = groupDefaults.settings[key];
                return (
                  <div key={key} className="space-y-2">
                    <Label
                      htmlFor={`group-default-${key}`}
                      className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground"
                    >
                      <Icon className="h-3.5 w-3.5" />
                      {label}
                    </Label>
                    {editable ? (
                      <select
                        id={`group-default-${key}`}
                        value={valueFor(key)}
                        disabled={saving}
                        onChange={(e) => setEdits((prev) => ({ ...prev, [key]: e.target.value }))}
                        className="w-full px-3 py-2 border border-border rounded-md bg-background"
                      >
                        <option value="">
                          {isTopLevel ? 'Use platform default' : 'Inherit from parent group'}
                        </option>
                        {optionsFor(key).map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    ) : (
                      <p className="font-semibold text-blue-900 dark:text-blue-100">
                        {entry?.display || '—'}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      In use: {entry?.display || '—'}
                      {entry?.source ? ` (from ${entry.source})` : ''}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* W2-T1: which clock judges "today" for delivery metrics. Sits with
                the timezone it refers to, not with the snapshot schedule. The
                backend reads a site's OWN group's flag (no ancestor walk), so
                the switch is offered only for that level; a parent level gets a
                pointer instead of a control that would save and do nothing. */}
            {target && target.id !== levels[0]?.id ? (
              <p className="text-xs text-muted-foreground" data-testid="group-clock-parent-note">
                The group clock is set on each site&apos;s own group, not here.
              </p>
            ) : (
            <div className="flex items-center justify-between gap-4 rounded-md border border-input bg-background px-4 py-3">
              <div>
                <Label htmlFor="group-default-clock" className="text-sm font-semibold">
                  Judge delivery metrics on the group clock
                </Label>
                <p className="text-xs text-muted-foreground" data-testid="group-clock-help">
                  Which timezone&apos;s date is &quot;today&quot; for arrears, for when a
                  scheduling-agreement line leaves its window, and for month and year-to-date cut-offs.
                  On-time and OTIF verdicts are not affected.{' '}
                  {groupClock
                    ? `On: the group timezone (${groupTimezoneDisplay}) for every site, even those overriding their timezone below.`
                    : 'Off (default): each site\'s own timezone.'}
                </p>
              </div>
              <Switch
                id="group-default-clock"
                aria-label="Group clock"
                checked={groupClock}
                disabled={!editable || saving}
                onCheckedChange={(checked) => setClockEdit(checked)}
              />
            </div>
            )}

            {editable && (
              <Button size="sm" onClick={handleSave} disabled={!dirty || saving}>
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save group defaults
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
